import { type UserAgreementUserProvidedValues } from '@capsicum/common';
import {
	useGetRequirementStateByIdentifierQuery,
	useSubmitRequirementStateByIdentifierQuery
} from '@capsicum/features/regulations/shared';
import { useEffect, useState } from 'react';

const UPDATE_MARKETING_EMAIL_CONSENT_DEBOUNCE_TIMEOUT = 500;

export const useMarketingConsent = () => {
	const { data: marketingEmailConsentResult, isFetching } = useGetRequirementStateByIdentifierQuery({
		requirementIdentifier: 'marketing_email_consent'
	});
	const {
		data: updateMarketingEmailConsentResponse,
		error: onMutationError,
		mutate: mutateMarketingEmailConsent
	} = useSubmitRequirementStateByIdentifierQuery();
	const [debouceTimeoutId, setDebouceTimeoutId] = useState(null as NodeJS.Timeout | null);
	const [isInitialValueSet, setIsInitialValueSet] = useState(false);
	const [isMarketingEmailConsentChecked, setMarketingEmailConsentChecked] = useState(false);
	const [isMarketingEmailConsentEnabled, setMarketingEmailConsentEnabled] = useState(false);

	const updateCheckboxWithResponseValue = (accepted: boolean) => {
		setMarketingEmailConsentChecked(accepted);
		setMarketingEmailConsentEnabled(accepted);
	};

	useEffect(() => {
		const response = marketingEmailConsentResult?.userProvidedValues as undefined | UserAgreementUserProvidedValues;

		updateCheckboxWithResponseValue(Boolean(response?.accepted));
		setTimeout(() => setIsInitialValueSet(true), 400);
	}, [marketingEmailConsentResult]);

	useEffect(() => {
		if (onMutationError) {
			updateCheckboxWithResponseValue(isMarketingEmailConsentEnabled);
		}
	}, [onMutationError]);

	useEffect(() => {
		const response = updateMarketingEmailConsentResponse?.data?.userProvidedValues as
			| undefined
			| UserAgreementUserProvidedValues;

		if (updateMarketingEmailConsentResponse?.data?.userProvidedValues) {
			updateCheckboxWithResponseValue(Boolean(response?.accepted));
		}
	}, [updateMarketingEmailConsentResponse]);

	const setMarketingEmailConsent = (accepted: boolean) => {
		setMarketingEmailConsentChecked(accepted);
		if (debouceTimeoutId) clearTimeout(debouceTimeoutId);

		const timeoutId = setTimeout(() => {
			if (accepted !== isMarketingEmailConsentEnabled) {
				mutateMarketingEmailConsent({
					requirementData: {
						accepted
					},
					requirementIdentifier: 'marketing_email_consent'
				});
			}
		}, UPDATE_MARKETING_EMAIL_CONSENT_DEBOUNCE_TIMEOUT);

		setDebouceTimeoutId(timeoutId);
	};

	const switchMarketingConsentAcceptance = () => {
		setMarketingEmailConsentChecked(!isMarketingEmailConsentChecked);
		setMarketingEmailConsent(!isMarketingEmailConsentChecked);
	};

	return {
		fetchMarketingEmailConsentLoading: isFetching || !isInitialValueSet,
		isMarketingEmailConsentChecked,
		switchMarketingConsentAcceptance,
		updateMarketingEmailConsentResponse
	};
};
